/* Cart estimates only. Does not select or charge a checkout delivery method. */
if (!customElements.get('vera-shipping')) {
  let queue = Promise.resolve();
  const storageKey = 'vera-shipping-preference-v1';
  const root = window.Shopify?.routes?.root || '/';
  const readPreference = () => {
    try { return JSON.parse(localStorage.getItem(storageKey) || '{}'); }
    catch { return {}; }
  };
  const savePreference = (value) => {
    try { localStorage.setItem(storageKey, JSON.stringify(value)); } catch { /* Optional storage. */ }
  };
  const request = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin', ...options,
      signal: AbortSignal.timeout(15000)
    });
    const body = await response.text();
    let result;
    try { result = body ? JSON.parse(body) : null; }
    catch { throw new Error('Invalid JSON response (HTTP ' + response.status + ')'); }
    if (!response.ok) {
      const endpoint = new URL(url, window.location.origin).pathname;
      const detail = result?.errors || result?.description || result?.message || result;
      const explanation = detail ? (typeof detail === 'string' ? detail : JSON.stringify(detail)) : 'Empty response body';
      throw new Error('HTTP ' + response.status + ' ' + endpoint + ': ' + explanation.slice(0, 1600));
    }
    return result;
  };

  class VeraShipping extends HTMLElement {
    static get observedAttributes() { return ['data-cart-state']; }
    connectedCallback() {
      if (this.initialized) {
        // A cart update can detach and reconnect this same element.
        // disconnectedCallback invalidates its pending request, so restart it.
        clearTimeout(this.refreshTimer);
        this.refreshTimer = setTimeout(() => {
          if (this.isConnected) this.calculate();
        }, 350);
        return;
      }
      this.initialized = true;
      this.version = 0;
      this.messages = JSON.parse(this.querySelector('[data-messages]').textContent);
      this.country = this.querySelector('[data-country-input]');
      this.zip = this.querySelector('[data-zip]');
      this.province = this.querySelector('[data-province]');
      this.select = this.querySelector('[data-rates]');
      this.status = this.querySelector('[data-status]');
      const saved = readPreference();
      const countries = Array.from(this.country.options, option => option.value);
      this.country.value = countries.length === 1 ? countries[0]
        : countries.includes(saved.country) ? saved.country
        : countries.includes(this.dataset.country) ? this.dataset.country : countries[0];
      this.country.disabled = countries.length === 1;
      if (countries.length === 1) this.country.closest("label").style.display = "none";
      if (saved.country === this.country.value) {
        this.zip.value = saved.zip || '';
        this.province.value = saved.province || '';
      }
      this.querySelector('[data-calculate]').addEventListener('click', () => this.calculate());
      this.country.addEventListener('change', () => {
        this.zip.value = '';
        this.province.value = '';
        this.calculate();
      });
      [this.zip, this.province].forEach(input => {
        input.addEventListener('input', () => {
          this.version++;
          this.clear();
          this.status.textContent = this.messages.address;
        });
        input.addEventListener('keydown', event => {
          if (event.key === 'Enter') { event.preventDefault(); this.calculate(); }
        });
      });
      this.select.addEventListener('change', () => this.showRate());
      // Section rendering changes data-cart-state when items, discounts or currency change.
      this.calculate();
    }
    disconnectedCallback() { this.version++; }
    attributeChangedCallback(name, before, after) {
      if (before !== after && this.initialized && this.isConnected) {
        clearTimeout(this.refreshTimer);
        this.version++;
        this.clear();
        this.refreshTimer = setTimeout(() => this.calculate(), 350);
      }
    }
    clear() {
      const singleCountry = this.country.options.length === 1;
      this.zip.closest("label").style.display = singleCountry ? "none" : "block";
      this.province.closest("label").style.display = singleCountry ? "none" : "block";
      this.querySelector("[data-calculate]").hidden = false;
      this.querySelector('[data-result]').hidden = true;
      this.querySelector('[data-rates-label]').hidden = true;
      this.rates = [];
    }
    calculate() {
      const version = ++this.version;
      this.clear();
      this.status.textContent = this.messages.loading;
      const address = {
        country: this.country.value,
        zip: this.country.options.length === 1 ? '188' : (this.zip.value.trim() || '188'),
        province: this.country.options.length === 1 ? '' : this.province.value.trim()
      };
      const previous = readPreference();
      savePreference({ ...address, method: previous.country === address.country ? previous.method : null });
      // Serialize rate requests, including a possible second calculator in the cart drawer.
      queue = queue.catch(() => {}).then(async () => {
        if (!this.isConnected || version !== this.version) return;
        try {
          const cart = await request(root + 'cart.js');
          if (!cart.requires_shipping || !cart.item_count) {
            this.status.textContent = '';
            return;
          }
          const params = new URLSearchParams();
          Object.entries(address).forEach(([key, value]) => {
            if (value) params.set('shipping_address[' + key + ']', value);
          });
          const query = '?' + params.toString();
          await request(root + 'cart/prepare_shipping_rates.json' + query, { method: 'POST' });
          let result = null;
          for (let attempt = 0; attempt < 12; attempt++) {
            if (!this.isConnected || version !== this.version) return;
            result = await request(root + 'cart/async_shipping_rates.json' + query);
            if (result !== null) break;
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
          if (!this.isConnected || version !== this.version) return;
          if (!result || !Array.isArray(result.shipping_rates)) throw new Error('rates');
          this.rates = result.shipping_rates;
          this.cart = cart;
          if (!this.rates.length) {
            this.status.textContent = this.messages.none;
            return;
          }
          this.select.replaceChildren();
          this.rates.forEach((rate, index) => {
            const option = document.createElement('option');
            option.value = String(index);
            option.textContent = rate.name + ' · ' + this.money(Number(rate.price));
            this.select.append(option);
          });
          const saved = readPreference();
          const index = this.rates.findIndex(rate => rate.name === saved.method);
          this.select.value = String(index >= 0 ? index : 0);
          this.querySelector('[data-rates-label]').hidden = this.rates.length === 1;
          const simple = this.country.options.length === 1 && this.rates.length === 1;
          const singleCountry = this.country.options.length === 1;
          this.zip.closest("label").style.display = singleCountry ? "none" : "block";
          this.province.closest("label").style.display = singleCountry ? "none" : "block";
          this.querySelector("[data-calculate]").hidden = simple;
          this.status.textContent = this.rates.length === 1 ? '' : this.messages.ready;
          this.showRate();
        } catch (error) {
          if (version !== this.version || !this.isConnected) return;
          this.clear();
          this.status.textContent = this.messages.error + ' [' + error.message + ']';
        }
      });
    }
    money(value) {
      return new Intl.NumberFormat(this.dataset.locale || 'en', {
        style: 'currency', currency: this.cart.currency
      }).format(value);
    }
    showRate() {
      const rate = this.rates[Number(this.select.value)];
      if (!rate || !Number.isFinite(Number(rate.price))) return;
      const shipping = Number(rate.price);
      this.querySelector('[data-shipping-price]').textContent = this.money(shipping);
      this.querySelector('[data-estimate]').textContent = this.money(this.cart.total_price / 100 + shipping);
      this.querySelector('[data-result]').hidden = false;
      savePreference({
        country: this.country.value, zip: this.zip.value.trim(),
        province: this.province.value.trim(), method: rate.name
      });
    }
  }
  customElements.define('vera-shipping', VeraShipping);
}
